import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { TOOL_IDS } from '@adsk/alloy-provider';
import { ISpeechBubbleIcon } from '@adsk/alloy-react-icon';
import { EntityPickerModalTopBar } from '@adsk/acc-reference-picker';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from '@adsk/alloy-error-page';
import { TabTitle } from '@adsk/exo-skeleton-react';
import ActivityCard from './components/ActivityCard';
import { RFICustomDataView } from './components/CustomDataView';
import { RfiEntityBlock } from './components/EntityBlock';
import EntityMenuItem from './components/EntityMenuItem';
import FiltersPanel from './components/FiltersPanel/FiltersPanel';
import { getSdk } from './components/helpers/getSdk';
import RFIMigration from './components/helpers/RFIMigration';
import { RfisProvider } from '../providers';
import { translate } from '../helpers';
import { useGetRfisMaintenanceMode } from '../hooks';
import { RfiCreateForm } from '../RfiCreateForm';
import RfiFormProvider from '../RfiCreateForm/RfiFormProvider';
import { withRFIsErrorBoundary } from '../components';
import { queryEntities } from './helpers';

const Provider = ({ children }) => <RfisProvider>{children}</RfisProvider>;
Provider.propTypes = {
  children: PropTypes.node,
};
Provider.displayName = 'RfisProvider';

const PickerModalTitle = ({ showTabTitle = true }) => {
  const title = translate('rpPlugin.ADD_RFI_REFERENCE');
  return (
    <Fragment>
      {showTabTitle && <TabTitle value={title} />}
      {title}
    </Fragment>
  );
};

PickerModalTitle.propTypes = {
  showTabTitle: PropTypes.bool,
};

const RFIsPlugin = {
  metadata: {
    domain: 'autodesk-bim360-rfi',
    type: 'rfi',
  },

  FiltersPanel: (props) => <FiltersPanel {...props} />,

  renderIcon: (p) => <ISpeechBubbleIcon {...p} />,

  toolId: TOOL_IDS.RFIS,

  showSearchBox: true,

  is3LOSupported: true,

  PluralText: () => translate('rpPlugin.RFIs'),

  PickerModalTitle,

  AddRelationshipsButtonText: ({ count }) => {
    const translateKey = count === 1 ? 'rpPlugin.ADD_RFI' : 'rpPlugin.ADD_RFIS';
    return translate(translateKey);
  },

  SelectedCount: ({ count }) => translate('rpPlugin.RFIS_SELECTED', { count }),

  CreateNewButtonText: () => translate('rpPlugin.CREATE_NEW'),

  queryEntities,

  getEntitiesByIds: async ({ ids, projectId }) => {
    const sdk = getSdk(projectId);
    const { results } = await sdk.RFIs.getAll({
      limit: 200,
      filterBy: {
        id: ids,
      },
    });

    const deserializeFunc = RFIMigration.deserialize;
    const deserializedRfis = results.map(deserializeFunc);
    return {
      data: deserializedRfis,
    };
  },

  CustomTopBar: (props) => {
    const isMaintenanceMode = useGetRfisMaintenanceMode();
    return isMaintenanceMode ? null : <EntityPickerModalTopBar {...props} />;
  },

  canCreate: ({ projectId }) => {
    const sdk = getSdk(projectId);
    return sdk.Users.canCreate();
  },

  /**
   * additional props may be passed to `CreateNewEntityPanel` through `createFormProps`
   * see https://git.autodesk.com/BIM360/acc-rfis-web-lib/blob/develop/src/acc-rfis/RfiCreateForm/RfiCreateForm.jsx#L75
   */
  CreateNewEntityPanel: (props) => (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <RfiFormProvider {...props}>
        <RfiCreateForm {...props} />
      </RfiFormProvider>
    </ErrorBoundary>
  ),
  Provider,
  EntityBlock: withRFIsErrorBoundary((props) => <RfiEntityBlock {...props} />),
  EntityMenuItem,
  CustomDataView: (props) => <RFICustomDataView {...props} />,
  ActivityCard,
  CustomRemoveEntityModalTitle: () => translate('rpPlugin.REMOVE_REFERENCE'),
  CustomRemoveEntityModalContent: ({ entity }) => {
    const { customIdentifier, title } = entity;
    const formattedCustomIdentifier =
      customIdentifier && customIdentifier !== '-' ? `#${customIdentifier} - ` : '';

    return translate('rpPlugin.CONFIRM_REMOVE_RFI', {
      rfi: formattedCustomIdentifier + title,
    });
  },
};
export default RFIsPlugin;
